import {
  UseMutateFunction,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import { createContext, useContext, useState } from "react";

import { deleteImageByUrl, refreshImageByUrl } from "../api/mediaReference.api";
import { useToast } from "./ToastContext";

export const ContentActionsWrapper = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  const [imageUrlUpdating, setImageUrlUpdating] = useState<string | null>(null);

  const { mutate: refresh, isPending } = useMutation({
    mutationFn: ({
      url,
      prompt,
      newImage,
    }: {
      url: string;
      prompt: string;
      newImage: boolean;
    }) => {
      setImageUrlUpdating(url);

      return refreshImageByUrl({ url, prompt, newImage });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["content"] });

      showToast("Content refreshed", "success");
    },
    onError: (err) => {
      console.error("Error refreshing content:", err);
      const message =
        err instanceof Error ? err.message : "Failed to refresh content";
      showToast(message, "error");
    },
    onSettled: () => {
      setImageUrlUpdating(null);
    },
  });

  const { mutate: remove, isPending: isRemoving } = useMutation({
    mutationFn: deleteImageByUrl,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["content"] });
      showToast("Content deleted", "success");
    },
    onError: (err) => {
      console.error("Error deleting content:", err);
      const message =
        err instanceof Error ? err.message : "Failed to delete content";
      showToast(message, "error");
    },
  });

  return (
    <ContentActionsContext.Provider
      value={{
        refresh,
        remove,
        loading: isPending || isRemoving,
        imageUrlUpdating,
      }}
    >
      {children}
    </ContentActionsContext.Provider>
  );
};

export const ContentActionsContext = createContext({
  refresh: {} as UseMutateFunction<
    string,
    Error,
    {
      url: string;
      prompt: string;
      newImage: boolean;
    },
    unknown
  >,
  remove: {} as UseMutateFunction<void, Error, string, unknown>,
  loading: false,
  imageUrlUpdating: {} as string | null,
});

export const useContentActions = () => useContext(ContentActionsContext);

